import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSoftphone } from '../contexts/SoftphoneContext';
import { useSantralGorusmeOptional } from '../contexts/SantralGorusmeContext';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import useKuyrukDurumu from '../hooks/useKuyrukDurumu';
import useSantralDurum from '../hooks/useSantralDurum';
import useMusteriTelefonMap from '../hooks/useMusteriTelefonMap';
import { apiRequest } from '../services/apiClient';
import { kuyrukUstlen, kuyrukBirak, geciciKuyruklariTemizle } from '../services/santralYonetimService';
import { formatTelefonGoster, normalizeTurkishPhone } from '../utils/phoneNormalize';
import { getSantralTabLabel } from '../utils/santralTabLabels';
import SantralAramaSekmesi from './SantralAramaSekmesi';
import './SantralPanel.css';

const beklemeFormat = (sn) => {
  const s = Math.max(0, Math.floor(Number(sn) || 0));
  const d = Math.floor(s / 60);
  const sec = s % 60;
  return `${d}:${String(sec).padStart(2, '0')}`;
};

const kuyrukEtiket = (k) => {
  const kisa = (k.ad || '').replace(/^.*?-queue-/i, '') || k.ad;
  return k.numara ? `${kisa} (#${k.numara})` : kisa;
};

const DURUM_LABEL = {
  empty: 'Müsait',
  ringing: 'Çalıyor',
  onhook: 'Görüşmede',
  unregister: 'Çevrimdışı',
};

/** Personel durum etiketleri (müşteri hizmetleri durumu) */
const PERSONEL_DURUM = {
  musait: { label: 'Müsait', icon: 'check-circle' },
  mesgul: { label: 'Meşgul', icon: 'mug-hot' },
  gorusmede: { label: 'Görüşmede', icon: 'phone' },
  cevrimdisi: { label: 'Çevrimdışı', icon: 'power-off' },
};

const CONNECTION_LABEL = {
  idle: 'Bağlı değil',
  starting: 'Başlatılıyor…',
  registering: 'Kayıt olunuyor…',
  registered: 'Hazır',
  incoming: 'Gelen arama',
  answering: 'Cevaplanıyor…',
  active: 'Görüşmede',
  error: 'Hata',
};

const LOG_SEVIYE_ICON = {
  error: 'exclamation-circle',
  warning: 'exclamation-triangle',
  info: 'info-circle',
  success: 'check-circle',
};

const saatFormat = (ts) => new Date(ts).toLocaleTimeString('tr-TR', {
  hour: '2-digit',
  minute: '2-digit',
});

const saatSaniyeFormat = (ts) => new Date(ts).toLocaleTimeString('tr-TR', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

const telefonEsit = (a, b) => {
  const na = normalizeTurkishPhone(a);
  const nb = normalizeTurkishPhone(b);
  if (na.length === 10 && nb.length === 10) return na === nb;
  return String(a || '').trim() === String(b || '').trim();
};

const baglantiDurumMetni = (connectionState, isRegistered, hasActive, hasIncoming) => {
  if (hasActive) return 'Görüşmede';
  if (hasIncoming) return 'Gelen arama';
  if (isRegistered) return 'Santral bağlantısı aktif';
  return CONNECTION_LABEL[connectionState] || 'Bağlanıyor…';
};

/**
 * Sağ kenar Netgsm/Netsantral WebRTC santral paneli.
 *
 * @param {boolean} [izleme=false] Salt-izleme modu (dahili atanmamış admin):
 *   softphone başlatılmaz; müsaitlik / bağlan / bağlantıyı kes / cevapla gizlenir;
 *   yalnızca anlık kuyruk akışı gösterilir.
 */
const SantralPanel = ({ isOpen, izleme = false, onToggle, onClose, onIncomingChange }) => {
  const {
    connectionState,
    extensionLabel,
    incomingCall,
    activeCall,
    isRegistered,
    hasIncoming,
    hasActive,
    hasSipSession,
    isAnswering,
    isTransferring,
    micActive,
    micMuted,
    startSoftphone,
    stopSoftphone,
    answerCall,
    rejectCall,
    cancelIncomingCall,
    hangupCall,
    dialCall,
    isDialing,
    toggleMicMute,
    transferCall,
    santralLoglari,
    gelenCagriGecmisi,
    appendSantralLog,
    clearSantralLoglari,
    kaydetGelenCagri,
    startRingtone,
    stopRingtone,
  } = useSoftphone();

  const navigate = useNavigate();
  const santralGorusme = useSantralGorusmeOptional();
  const santralSession = santralGorusme?.session;
  const { confirm } = useConfirmDialog();

  const [altSekme, setAltSekme] = useState('kuyruk');
  const [kuyrukAcik, setKuyrukAcik] = useState(true);
  const { kuyruklar, yukleniyor: kuyrukYukleniyor, hata: kuyrukHata, sonGuncelleme, apiAktif, yenile } = useKuyrukDurumu({ aktif: isOpen });

  const aramaTelefonlari = useMemo(() => {
    const nums = [];
    if (incomingCall?.telefon) nums.push(incomingCall.telefon);
    if (activeCall?.telefon) nums.push(activeCall.telefon);
    gelenCagriGecmisi.forEach((g) => {
      if (g.telefon) nums.push(g.telefon);
    });
    kuyruklar.forEach((k) => {
      (k.beklemedeCallerlar || []).forEach((c) => {
        if (c.numara) nums.push(c.numara);
      });
    });
    return nums;
  }, [incomingCall?.telefon, activeCall?.telefon, gelenCagriGecmisi, kuyruklar]);

  const { getAdSoyad } = useMusteriTelefonMap(aramaTelefonlari, { aktif: isOpen });

  const {
    durum: personelDurum,
    sebep: personelSebep,
    dahili: personelDahili,
    durumDegistir,
    yukleniyor: durumYukleniyor,
  } = useSantralDurum({ aktif: isRegistered, gorusmedeAktif: hasActive });

  const showSkResume = !izleme && (hasActive || santralSession?.gorusmeKaydiId);

  const handleResumeServiceRecord = useCallback(() => {
    const session = santralGorusme?.session;
    if (!session?.gorusmeKaydiId) return;
    navigate('/service-records/create', {
      state: {
        fromSantral: true,
        gorusmeKaydiId: session.gorusmeKaydiId,
        musteriId: session.musteriId || undefined,
        telefon: session.santralTelefon || undefined,
        initialTab: session.activeTab || (session.musteriId ? 'gorusme-hub' : 'customer-search'),
      },
    });
  }, [navigate, santralGorusme]);

  const [ustleniyor, setUstleniyor] = useState(false);
  const [ustlenilenKuyruk, setUstlenilenKuyruk] = useState(null);
  const [bekleyenUstlen, setBekleyenUstlen] = useState(null);
  const ustlenilenKuyrukVeri = useMemo(
    () => kuyruklar.find((k) => k.ad === ustlenilenKuyruk),
    [kuyruklar, ustlenilenKuyruk]
  );
  const ustlenTimeoutRef = useRef(null);
  const gorustuRef = useRef(false);
  const geciciTemizlendiRef = useRef(false);

  const [mesgulSebepAcik, setMesgulSebepAcik] = useState(false);
  const [mesgulSebep, setMesgulSebep] = useState('');
  /** Reddet sonrası kuyruktan tekrar üstlenme — { kuyrukAd, telefon } */
  const [sonReddedilenArama, setSonReddedilenArama] = useState(null);
  const pickupDogrulamaRef = useRef(null);
  const [logModalAcik, setLogModalAcik] = useState(false);
  const [gelenModalAcik, setGelenModalAcik] = useState(false);
  const sonKaydedilenAramaRef = useRef(null);

  const mesgulOnayla = () => {
    durumDegistir('mesgul', mesgulSebep.trim());
    setMesgulSebepAcik(false);
    setMesgulSebep('');
  };

  // Çağrı aktarma (transfer)
  const [transferAcik, setTransferAcik] = useState(false);
  const [hedefler, setHedefler] = useState([]);
  const [hedefYukleniyor, setHedefYukleniyor] = useState(false);

  const hedefleriYukle = useCallback(async () => {
    setHedefYukleniyor(true);
    try {
      const data = await apiRequest('/api/santral/transfer-hedefleri', { method: 'GET', skipCsrf: true });
      setHedefler(data?.veri?.hedefler || []);
    } catch {
      setHedefler([]);
    } finally {
      setHedefYukleniyor(false);
    }
  }, []);

  const transferAc = () => {
    setTransferAcik((acik) => {
      const yeni = !acik;
      if (yeni) hedefleriYukle();
      return yeni;
    });
  };

  const hedefeAktar = (numara) => {
    transferCall(numara);
    setTransferAcik(false);
  };

  // Panel (AP/SP) açıldığında çağrı sistemini bir kez otomatik başlat.
  // Kullanıcı manuel "Bağlantıyı kes" derse veya hata olursa tekrar otomatik denenmez.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (izleme) return; // İzleme modunda softphone başlatılmaz
    if (autoStartedRef.current) return;
    if (connectionState === 'idle') {
      autoStartedRef.current = true;
      startSoftphone();
    }
  }, [izleme, connectionState, startSoftphone]);

  // Panel kayıt olunca üstlenebilir kuyruklardaki takılı geçici üyelikleri temizle (admin OPERATOR'da kalmasın)
  useEffect(() => {
    if (izleme || !isRegistered || !isOpen || geciciTemizlendiRef.current) return;
    geciciTemizlendiRef.current = true;
    geciciKuyruklariTemizle().catch(() => {});
  }, [izleme, isRegistered, isOpen]);

  // Geçici kuyruk üyeliğini görüşme bitince sonlandır
  useEffect(() => {
    if (hasActive) gorustuRef.current = true;
    if (!hasActive && gorustuRef.current && ustlenilenKuyruk) {
      kuyrukBirak(ustlenilenKuyruk).catch(() => {});
      setUstlenilenKuyruk(null);
      setBekleyenUstlen(null);
      setUstleniyor(false);
      gorustuRef.current = false;
    }
  }, [hasActive, ustlenilenKuyruk]);

  useEffect(() => () => {
    if (ustlenTimeoutRef.current) clearTimeout(ustlenTimeoutRef.current);
  }, []);

  const cagriMesgul = hasActive || hasIncoming || isAnswering || ustleniyor || !!ustlenilenKuyruk;

  const ustlenmeyiIptal = useCallback(async (kuyrukAdOverride) => {
    if (ustlenTimeoutRef.current) {
      clearTimeout(ustlenTimeoutRef.current);
      ustlenTimeoutRef.current = null;
    }
    const kuyrukAd = kuyrukAdOverride || ustlenilenKuyruk;
    stopRingtone();
    if (kuyrukAd) {
      try {
        await kuyrukBirak(kuyrukAd);
      } catch {
        // Kuyruk üyeliği zaten kalkmış olabilir
      }
    }
    setUstlenilenKuyruk(null);
    setBekleyenUstlen(null);
    setUstleniyor(false);
    pickupDogrulamaRef.current = null;
  }, [ustlenilenKuyruk, stopRingtone]);

  const cagriUstlen = useCallback(async (kuyruk) => {
    if (cagriMesgul) return;
    const ilkBekleyen = kuyruk.beklemedeCallerlar?.[0] || null;
    const toplamBekleyen = kuyruk.beklemedeCallerlar?.length ?? 0;
    setBekleyenUstlen(ilkBekleyen);
    setUstleniyor(true);
    pickupDogrulamaRef.current = null;
    if (ilkBekleyen?.numara) {
      const siraMetin = toplamBekleyen > 1
        ? ` (kuyrukta ${toplamBekleyen} bekleyen; NetGSM sıradakini yönlendirir)`
        : '';
      appendSantralLog(
        `Çağrı üstleniliyor — beklenen arayan: ${formatTelefonGoster(ilkBekleyen.numara)}${siraMetin}`,
        'info',
      );
    }
    try {
      const r = await kuyrukUstlen(kuyruk.ad);
      if (r?.veri?.zatenUye) {
        setUstleniyor(false);
        setBekleyenUstlen(null);
        return;
      }
      setUstlenilenKuyruk(kuyruk.ad);
      setUstleniyor(false);
      if (ustlenTimeoutRef.current) clearTimeout(ustlenTimeoutRef.current);
      ustlenTimeoutRef.current = setTimeout(() => {
        ustlenmeyiIptal(kuyruk.ad);
        appendSantralLog('Çağrı üstlenme zaman aşımına uğradı.', 'warning');
      }, 45000);
    } catch (err) {
      setUstleniyor(false);
      setBekleyenUstlen(null);
      appendSantralLog(err?.message || 'Çağrı üstlenilemedi', 'error');
    }
  }, [appendSantralLog, cagriMesgul, ustlenmeyiIptal]);

  /** Üstlenme sonrası SIP INVITE numarasının kuyruk sırasındaki beklenen numara ile eşleşmesini logla */
  useEffect(() => {
    if (!hasIncoming || !incomingCall?.telefon) return undefined;
    const beklenen = bekleyenUstlen?.numara
      || ustlenilenKuyrukVeri?.beklemedeCallerlar?.[0]?.numara;
    if (!beklenen || !ustlenilenKuyruk) return undefined;

    const gelen = incomingCall.telefon;
    const key = `${ustlenilenKuyruk}:${normalizeTurkishPhone(gelen)}:${normalizeTurkishPhone(beklenen)}`;
    if (pickupDogrulamaRef.current === key) return undefined;
    pickupDogrulamaRef.current = key;

    if (telefonEsit(gelen, beklenen)) {
      appendSantralLog(
        `Üstlenme doğrulandı: gelen arama (${formatTelefonGoster(gelen)}) kuyruk sırasındaki numara ile eşleşti.`,
        'success',
      );
    } else {
      appendSantralLog(
        `Üstlenme uyarısı: beklenen ${formatTelefonGoster(beklenen)}, gelen ${formatTelefonGoster(gelen)}. NetGSM sıradaki çağrıyı yönlendirdi.`,
        'warning',
      );
    }
    return undefined;
  }, [
    appendSantralLog,
    bekleyenUstlen,
    hasIncoming,
    incomingCall,
    ustlenilenKuyruk,
    ustlenilenKuyrukVeri,
  ]);

  const benimDahiliNo = personelDahili?.numara || extensionLabel || '';
  const kuyrukCaliyor = Boolean(
    benimDahiliNo
    && kuyruklar.some((k) => (
      (!ustlenilenKuyruk || k.ad === ustlenilenKuyruk)
      && k.dahililer?.some(
        (d) => String(d.dahili) === String(benimDahiliNo) && d.durum === 'ringing'
      )
    ))
  );
  const kuyrukBekleyenIlk = bekleyenUstlen || ustlenilenKuyrukVeri?.beklemedeCallerlar?.[0] || null;
  const pickupAktif = ustleniyor || !!ustlenilenKuyruk;
  const gelenAramaTelefon = incomingCall?.telefon || kuyrukBekleyenIlk?.numara || null;
  /** Yalnızca üstlenme başlatıldığında veya gerçek SIP INVITE geldiğinde göster — kuyruk API "çalıyor" tek başına yetmez */
  const gelenAramaGoster = !hasActive && (hasIncoming || isAnswering || pickupAktif);
  const gelenAramaKaynakKuyruk = !hasIncoming && pickupAktif;
  const sipBekleniyor = gelenAramaKaynakKuyruk && !hasSipSession;
  const baglantiKesGoster = isRegistered && !hasActive && !gelenAramaGoster && !ustleniyor;

  useEffect(() => {
    onIncomingChange?.(gelenAramaGoster);
  }, [gelenAramaGoster, onIncomingChange]);

  useEffect(() => {
    if (!gelenAramaTelefon) return;
    const key = normalizeTurkishPhone(gelenAramaTelefon);
    if (!key || sonKaydedilenAramaRef.current === key) return;
    sonKaydedilenAramaRef.current = key;
    kaydetGelenCagri(gelenAramaTelefon);
  }, [gelenAramaTelefon, kaydetGelenCagri]);

  useEffect(() => {
    if (!gelenAramaGoster) {
      sonKaydedilenAramaRef.current = null;
    }
  }, [gelenAramaGoster]);

  const gelenAramaReddet = useCallback(async () => {
    const kuyrukAd = ustlenilenKuyruk;
    const telefon = gelenAramaTelefon;
    if (hasIncoming) rejectCall();
    await ustlenmeyiIptal();
    if (kuyrukAd && telefon) {
      setSonReddedilenArama({ kuyrukAd, telefon });
    }
  }, [gelenAramaTelefon, hasIncoming, rejectCall, ustlenilenKuyruk, ustlenmeyiIptal]);

  const gelenAramayiIptal = useCallback(async () => {
    const telefonMetin = gelenAramaTelefon ? formatTelefonGoster(gelenAramaTelefon) : 'Arayan';
    const ok = await confirm({
      title: 'Aramayı iptal et',
      message: `${telefonMetin} numaralı arayanın hattı tamamen kapatılacak (kuyruğa dönmez). Onaylıyor musunuz?`,
      variant: 'danger',
      confirmLabel: 'Aramayı iptal et',
      cancelLabel: 'Vazgeç',
      iconClass: 'fas fa-phone-slash',
    });
    if (!ok) return;
    await cancelIncomingCall();
    await ustlenmeyiIptal();
    setSonReddedilenArama(null);
  }, [cancelIncomingCall, confirm, gelenAramaTelefon, ustlenmeyiIptal]);

  useEffect(() => {
    if (hasActive || hasIncoming) setSonReddedilenArama(null);
  }, [hasActive, hasIncoming]);

  useEffect(() => {
    if (gelenAramaGoster && !hasIncoming) {
      startRingtone();
    }
  }, [gelenAramaGoster, hasIncoming, startRingtone]);

  useEffect(() => {
    if (!gelenAramaGoster) {
      stopRingtone();
    }
  }, [gelenAramaGoster, stopRingtone]);

  const kuyrukCevaplaGoster = (kuyruk, c, idx) => {
    if (izleme) return false;
    // NetGSM agentlogin sıradaki (FIFO) çağrıyı yönlendirir; belirli arayan seçimi API'si yok
    if (idx !== 0) return false;
    if (!kuyruk.ustlenebilir || !isRegistered || cagriMesgul || ustlenilenKuyruk || ustleniyor) return false;
    return true;
  };

  const kuyrukSiradaGoster = (kuyruk, idx) => {
    if (izleme || idx === 0) return false;
    if (!kuyruk.ustlenebilir || !isRegistered || cagriMesgul || ustlenilenKuyruk || ustleniyor) return false;
    return true;
  };

  useEffect(() => {
    if (hasIncoming && ustlenTimeoutRef.current) {
      clearTimeout(ustlenTimeoutRef.current);
      ustlenTimeoutRef.current = null;
    }
  }, [hasIncoming]);

  /** Arayan kapattığında veya kuyruk boşaldığında takılı üstlenme durumunu temizle */
  useEffect(() => {
    if (!ustlenilenKuyruk || hasIncoming || hasActive || isAnswering) return;
    const bekleyenVar = (ustlenilenKuyrukVeri?.beklemedeCallerlar?.length ?? 0) > 0;
    const santralCaliyor = kuyrukCaliyor && pickupAktif;
    if (!bekleyenVar && !santralCaliyor) {
      ustlenmeyiIptal();
    }
  }, [
    ustlenilenKuyruk,
    ustlenilenKuyrukVeri,
    kuyrukCaliyor,
    pickupAktif,
    hasIncoming,
    hasActive,
    isAnswering,
    ustlenmeyiIptal,
  ]);

  /** SIP INVITE gelmezse 20 sn sonra üstlenmeyi bırak */
  useEffect(() => {
    if (!ustlenilenKuyruk || hasIncoming || hasActive) return undefined;
    const t = setTimeout(() => {
      if (!hasIncoming) {
        ustlenmeyiIptal();
        appendSantralLog('Telefon bağlantısı kurulamadı. Lütfen tekrar deneyin.', 'warning');
      }
    }, 20000);
    return () => clearTimeout(t);
  }, [appendSantralLog, ustlenilenKuyruk, hasIncoming, hasActive, ustlenmeyiIptal]);

  useEffect(() => {
    if (!logModalAcik && !gelenModalAcik) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setLogModalAcik(false);
        setGelenModalAcik(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [logModalAcik, gelenModalAcik]);

  const isBusy = connectionState === 'starting' || connectionState === 'registering';
  const showStartButton = !isRegistered && connectionState !== 'error';
  const showRetry = connectionState === 'error';
  const baglantiMetni = baglantiDurumMetni(connectionState, isRegistered, hasActive, hasIncoming);

  return (
    <>
      <aside
        className={`santral-panel${isOpen ? ' santral-panel--open' : ''}`}
        id="santral-panel"
        aria-label="Santral paneli"
        aria-hidden={!isOpen}
      >
        <header className="santral-panel-header">
          <div className="santral-panel-header-top">
            <div className="santral-panel-title-wrap">
              <i className="fas fa-headset santral-panel-icon" aria-hidden />
              <div>
                <h2 className="santral-panel-title">Santral</h2>
                <p className="santral-panel-subtitle">
                  {izleme ? 'Canlı kuyruk akışı' : extensionLabel ? `Dahili ${extensionLabel}` : 'Netgsm WebRTC'}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="santral-panel-close"
              onClick={onClose}
              aria-label="Paneli kapat"
            >
              <i className="fas fa-times" aria-hidden />
            </button>
          </div>

          <div className="santral-agent-status" role="status" aria-live="polite">
            {izleme ? (
              <span className="santral-status-pill santral-status-pill--izleme">
                <i className="fas fa-eye" aria-hidden />
                İzleme modu
              </span>
            ) : (
              <span className={`santral-status-pill santral-status-pill--${connectionState}`}>
                <i
                  className={`fas fa-${
                    hasActive ? 'phone' : hasIncoming ? 'bell' : isRegistered ? 'check-circle' : 'circle'
                  }`}
                  aria-hidden
                />
                {CONNECTION_LABEL[connectionState] || connectionState}
              </span>
            )}
          </div>
        </header>

        <div className="santral-panel-body">
          {izleme ? (
            <section className="santral-izleme-banner" aria-live="polite">
              <i className="fas fa-eye" aria-hidden />
              <span>
                İzleme modundasınız. Anlık kuyruk hareketlerini görüntülüyorsunuz; çağrı
                yanıtlama ve müsaitlik işlemleri için size bir dahili atanması gerekir.
              </span>
            </section>
          ) : null}

          {!izleme && gelenAramaGoster ? (
            <section className="santral-incoming-call santral-incoming-call--sticky" aria-live="assertive">
              <p className="santral-incoming-label">
                {gelenAramaKaynakKuyruk && !hasIncoming ? 'Kuyruktan gelen arama' : 'Gelen arama'}
              </p>
              {gelenAramaTelefon ? (
                <h3 className="santral-incoming-phone">{formatTelefonGoster(gelenAramaTelefon)}</h3>
              ) : (
                <h3 className="santral-incoming-phone">Bağlantı kuruluyor…</h3>
              )}
              {gelenAramaTelefon && getAdSoyad(gelenAramaTelefon) ? (
                <p className="santral-incoming-name">{getAdSoyad(gelenAramaTelefon)}</p>
              ) : null}
              {!hasSipSession && gelenAramaKaynakKuyruk ? (
                <p className="santral-incoming-hint">
                  <i className="fas fa-spinner fa-spin" aria-hidden />
                  {kuyrukCaliyor
                    ? 'Telefonunuz çalıyor — cevaplamak için SIP bağlantısı bekleniyor…'
                    : 'Kuyruğa bağlanılıyor — santral yanıtı bekleniyor…'}
                </p>
              ) : null}
              <div className="santral-call-actions santral-call-actions--incoming">
                <button
                  type="button"
                  className="santral-action-btn santral-action-btn--answer"
                  onClick={answerCall}
                  disabled={isAnswering || sipBekleniyor}
                  title={sipBekleniyor ? 'SIP bağlantısı kurulunca cevaplayabilirsiniz' : undefined}
                >
                  <i className={`fas fa-${isAnswering ? 'spinner fa-spin' : 'phone'}`} aria-hidden />
                  <span>{isAnswering ? 'Cevaplanıyor…' : 'Cevapla'}</span>
                </button>
                <button
                  type="button"
                  className="santral-action-btn santral-action-btn--reject"
                  onClick={gelenAramaReddet}
                  title="Siz cevaplamazsınız; çağrı kuyrukta kalır — alttaki kuyruk listesinden Cevapla ile tekrar alabilirsiniz"
                >
                  <i className="fas fa-user-times" aria-hidden />
                  <span>Reddet</span>
                </button>
                <button
                  type="button"
                  className="santral-action-btn santral-action-btn--cancel-call"
                  onClick={gelenAramayiIptal}
                  title="Arayanın hattını tamamen sonlandırır"
                >
                  <i className="fas fa-phone-slash" aria-hidden />
                  <span>Aramayı iptal et</span>
                </button>
              </div>
            </section>
          ) : null}

          {!izleme && isRegistered ? (
            <section className="santral-durum-section">
              <div className="santral-durum-current">
                <span className={`santral-durum-pill santral-durum-pill--${personelDurum}`}>
                  <i className={`fas fa-${(PERSONEL_DURUM[personelDurum] || PERSONEL_DURUM.cevrimdisi).icon}`} aria-hidden />
                  {(PERSONEL_DURUM[personelDurum] || {}).label || personelDurum}
                  {personelDurum === 'mesgul' && personelSebep ? ` · ${personelSebep}` : ''}
                </span>
              </div>
              {personelDurum === 'gorusmede' ? (
                <p className="santral-durum-note">Çağrı sırasında durum otomatik yönetilir.</p>
              ) : (
                <div className="santral-durum-actions">
                  <button
                    type="button"
                    className={`santral-durum-btn santral-durum-btn--musait${personelDurum === 'musait' ? ' is-active' : ''}`}
                    onClick={() => durumDegistir('musait')}
                    disabled={durumYukleniyor || personelDurum === 'musait'}
                  >
                    <i className="fas fa-check-circle" aria-hidden /> Müsait
                  </button>
                  <button
                    type="button"
                    className={`santral-durum-btn santral-durum-btn--mesgul${personelDurum === 'mesgul' ? ' is-active' : ''}`}
                    onClick={() => setMesgulSebepAcik((v) => !v)}
                    disabled={durumYukleniyor}
                  >
                    <i className="fas fa-mug-hot" aria-hidden /> Meşgul
                  </button>
                  <button
                    type="button"
                    className={`santral-durum-btn santral-durum-btn--cevrimdisi${personelDurum === 'cevrimdisi' ? ' is-active' : ''}`}
                    onClick={() => durumDegistir('cevrimdisi')}
                    disabled={durumYukleniyor || personelDurum === 'cevrimdisi'}
                  >
                    <i className="fas fa-power-off" aria-hidden /> Çevrimdışı
                  </button>
                </div>
              )}
              {mesgulSebepAcik ? (
                <div className="santral-durum-sebep">
                  <input
                    type="text"
                    className="santral-durum-sebep-input"
                    placeholder="Sebep (opsiyonel): mola, toplantı…"
                    value={mesgulSebep}
                    onChange={(e) => setMesgulSebep(e.target.value)}
                    maxLength={100}
                  />
                  <button
                    type="button"
                    className="santral-durum-sebep-onay"
                    onClick={mesgulOnayla}
                    disabled={durumYukleniyor}
                  >
                    Meşgule al
                  </button>
                </div>
              ) : null}
            </section>
          ) : null}

          {!izleme && !isRegistered && !hasActive && !hasIncoming ? (
            <section className="santral-start-section">
              <i className="fas fa-phone-volume santral-start-icon" aria-hidden />
              <p className="santral-start-text">
                Gelen çağrıları tarayıcıdan yanıtlamak için çağrı sistemini başlatın.
                Mikrofon izni istenecek ve dahiliniz santrale kayıt olacaktır.
              </p>
              {showStartButton ? (
                <button
                  type="button"
                  className="santral-start-btn"
                  onClick={startSoftphone}
                  disabled={isBusy}
                >
                  <i className={`fas fa-${isBusy ? 'spinner fa-spin' : 'power-off'}`} aria-hidden />
                  {isBusy ? 'Başlatılıyor…' : 'Çağrı sistemini başlat'}
                </button>
              ) : null}
              {showRetry ? (
                <button type="button" className="santral-start-btn" onClick={startSoftphone}>
                  <i className="fas fa-redo" aria-hidden />
                  Tekrar dene
                </button>
              ) : null}
            </section>
          ) : null}

          {!izleme && hasActive && activeCall ? (
            <section className="santral-active-call" aria-live="polite">
              <span className="santral-active-call-pulse" aria-hidden />
              <p className="santral-active-call-label">Aktif görüşme</p>
              {micMuted ? (
                <p className="santral-mic-status santral-mic-status--off">
                  <i className="fas fa-microphone-slash" aria-hidden /> Mikrofon susturuldu
                </p>
              ) : micActive ? (
                <p className="santral-mic-status santral-mic-status--on">
                  <i className="fas fa-microphone" aria-hidden /> Mikrofon aktif
                </p>
              ) : (
                <p className="santral-mic-status santral-mic-status--off">
                  <i className="fas fa-microphone-slash" aria-hidden /> Mikrofon kapalı
                </p>
              )}
              <h3 className="santral-active-call-phone">{formatTelefonGoster(activeCall.telefon)}</h3>
              {getAdSoyad(activeCall.telefon) ? (
                <p className="santral-active-call-name">{getAdSoyad(activeCall.telefon)}</p>
              ) : null}
              <p className="santral-active-call-time">{activeCall.baslangic}</p>
              <div className="santral-call-actions santral-call-actions--active">
                <button
                  type="button"
                  className={`santral-action-btn santral-action-btn--mute${micMuted ? ' is-muted' : ''}`}
                  onClick={toggleMicMute}
                  title={micMuted ? 'Mikrofonu aç' : 'Mikrofonu sustur'}
                  aria-pressed={micMuted}
                >
                  <i className={`fas fa-microphone${micMuted ? '-slash' : ''}`} aria-hidden />
                  <span>{micMuted ? 'Aç' : 'Sustur'}</span>
                </button>
                <button
                  type="button"
                  className="santral-action-btn santral-action-btn--transfer"
                  onClick={transferAc}
                  disabled={isTransferring}
                  title="Aktar"
                >
                  <i className={`fas fa-${isTransferring ? 'spinner fa-spin' : 'share'}`} aria-hidden />
                  <span>{isTransferring ? 'Aktarılıyor…' : 'Aktar'}</span>
                </button>
                <button
                  type="button"
                  className="santral-action-btn santral-action-btn--hangup"
                  onClick={hangupCall}
                  title="Kapat"
                >
                  <i className="fas fa-phone-slash" aria-hidden />
                  <span>Kapat</span>
                </button>
              </div>

              {transferAcik ? (
                <div className="santral-transfer-list" role="menu">
                  <p className="santral-transfer-title">Aktarılacak dahili</p>
                  {hedefYukleniyor ? (
                    <p className="santral-transfer-info">
                      <i className="fas fa-spinner fa-spin" aria-hidden /> Yükleniyor…
                    </p>
                  ) : hedefler.length === 0 ? (
                    <p className="santral-transfer-info">Aktarılacak dahili bulunamadı.</p>
                  ) : (
                    <ul className="santral-transfer-items">
                      {hedefler.map((h) => (
                        <li key={h.numara}>
                          <button
                            type="button"
                            className="santral-transfer-item"
                            onClick={() => hedefeAktar(h.numara)}
                          >
                            <span className="santral-transfer-num">{h.numara}</span>
                            <span className="santral-transfer-name">
                              {h.displayName || h.personel || 'Dahili'}
                              {h.personel && h.displayName ? ` · ${h.personel}` : ''}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}
            </section>
          ) : null}

          {showSkResume ? (
            <section className="santral-sk-resume">
              <button
                type="button"
                className="santral-sk-resume-btn"
                onClick={handleResumeServiceRecord}
              >
                <i className="fas fa-clipboard-list" aria-hidden />
                Servis Kaydına Dön
              </button>
              {santralSession?.activeTab ? (
                <p className="santral-sk-resume-step">
                  Adım: {getSantralTabLabel(santralSession.activeTab)}
                </p>
              ) : null}
            </section>
          ) : null}

          {!izleme && baglantiKesGoster ? (
            <section className="santral-registered-idle">
              <button type="button" className="santral-stop-btn santral-arama-stop" onClick={stopSoftphone}>
                <i className="fas fa-stop-circle" aria-hidden />
                Bağlantıyı kes
              </button>
            </section>
          ) : null}

          {/* Alt sekme: Kuyruk / Arama */}
          <section className="santral-alt-bolum">
            <div className="santral-alt-sekmeler" role="tablist" aria-label="Santral alt menü">
              <button
                type="button"
                role="tab"
                id="santral-tab-kuyruk"
                aria-selected={altSekme === 'kuyruk'}
                aria-controls="santral-panel-kuyruk"
                className={`santral-alt-sekme${altSekme === 'kuyruk' ? ' is-active' : ''}`}
                onClick={() => setAltSekme('kuyruk')}
              >
                <i className="fas fa-layer-group" aria-hidden />
                Kuyruk
              </button>
              {!izleme ? (
                <button
                  type="button"
                  role="tab"
                  id="santral-tab-arama"
                  aria-selected={altSekme === 'arama'}
                  aria-controls="santral-panel-arama"
                  className={`santral-alt-sekme${altSekme === 'arama' ? ' is-active' : ''}`}
                  onClick={() => setAltSekme('arama')}
                >
                  <i className="fas fa-phone-alt" aria-hidden />
                  Arama
                </button>
              ) : null}
            </div>

            {altSekme === 'arama' && !izleme ? (
              <div
                id="santral-panel-arama"
                role="tabpanel"
                aria-labelledby="santral-tab-arama"
                className="santral-alt-sekme-icerik"
              >
                <SantralAramaSekmesi
                  isRegistered={isRegistered}
                  hasActive={hasActive}
                  hasIncoming={hasIncoming}
                  isDialing={isDialing}
                  dialCall={dialCall}
                  appendSantralLog={appendSantralLog}
                />
              </div>
            ) : (
              <div
                id="santral-panel-kuyruk"
                role="tabpanel"
                aria-labelledby="santral-tab-kuyruk"
                className="santral-queue-section"
              >
            <div className="santral-queue-header">
              <button
                type="button"
                className="santral-queue-header-toggle"
                onClick={() => setKuyrukAcik((v) => !v)}
                aria-expanded={kuyrukAcik}
              >
                <span>
                  <i className="fas fa-layer-group" aria-hidden /> Kuyruk Durumu
                </span>
                <i className={`fas fa-chevron-${kuyrukAcik ? 'up' : 'down'}`} aria-hidden />
              </button>
              {kuyrukYukleniyor ? (
                <span className="santral-queue-header-spinner" aria-hidden>
                  <i className="fas fa-spinner fa-spin" />
                </span>
              ) : (
                <button
                  type="button"
                  className="santral-queue-refresh"
                  onClick={() => yenile()}
                  title="Yenile"
                  aria-label="Kuyruk verilerini yenile"
                >
                  <i className="fas fa-sync-alt" aria-hidden />
                </button>
              )}
            </div>

            {kuyrukAcik ? (
              <div className="santral-queue-body">
                {apiAktif === false || kuyrukHata ? (
                  <p className="santral-queue-warning">
                    <i className="fas fa-exclamation-circle" aria-hidden />
                    {kuyrukHata || 'API yapılandırması eksik.'}
                  </p>
                ) : kuyruklar.length === 0 && !kuyrukYukleniyor ? (
                  <p className="santral-queue-empty">Kuyruk verisi bulunamadı.</p>
                ) : (
                  kuyruklar.map((kuyruk) => (
                    <div key={kuyruk.ad} className="santral-queue-card">
                      <div className="santral-queue-card-header">
                        <span className="santral-queue-name">{kuyrukEtiket(kuyruk)}</span>
                        <span className={`santral-queue-badge${kuyruk.bekleyenAdet > 0 ? ' santral-queue-badge--active' : ''}`}>
                          {kuyruk.bekleyenAdet} bekliyor
                        </span>
                      </div>
                      {kuyruk.aciklama ? (
                        <p className="santral-queue-desc">{kuyruk.aciklama}</p>
                      ) : null}
                      <div className="santral-queue-stats">
                        <span title="Tamamlanan"><i className="fas fa-check-circle" aria-hidden /> {kuyruk.tamamlanan}</span>
                        <span title="Terkedilen"><i className="fas fa-times-circle" aria-hidden /> {kuyruk.terk}</span>
                        <span title="Ort. bekleme"><i className="fas fa-clock" aria-hidden /> {kuyruk.ortBekleme}s</span>
                      </div>

                      {kuyruk.beklemedeCallerlar?.length > 0 ? (
                        <ul className="santral-waiting-list">
                          {kuyruk.beklemedeCallerlar.map((c, idx) => {
                            const reddedildiBu = Boolean(
                              sonReddedilenArama
                              && sonReddedilenArama.kuyrukAd === kuyruk.ad
                              && telefonEsit(c.numara, sonReddedilenArama.telefon),
                            );
                            return (
                            <li
                              key={`${c.numara}-${idx}`}
                              className={`santral-waiting-item${reddedildiBu ? ' santral-waiting-item--reddedildi' : ''}`}
                            >
                              <div className="santral-waiting-info">
                                <span className="santral-waiting-num">{formatTelefonGoster(c.numara)}</span>
                                {getAdSoyad(c.numara) ? (
                                  <span className="santral-waiting-customer">{getAdSoyad(c.numara)}</span>
                                ) : null}
                                <span className="santral-waiting-time">
                                  <i className="fas fa-hourglass-half" aria-hidden /> {beklemeFormat(c.bekleme)}
                                </span>
                              </div>
                              {kuyrukCevaplaGoster(kuyruk, c, idx) ? (
                                <button
                                  type="button"
                                  className="santral-waiting-answer"
                                  onClick={() => cagriUstlen(kuyruk)}
                                  title="Sıradaki çağrıyı üstlen ve cevapla"
                                  aria-label="Cevapla"
                                >
                                  <i className="fas fa-phone" aria-hidden />
                                  <span>Cevapla</span>
                                </button>
                              ) : kuyrukSiradaGoster(kuyruk, idx) ? (
                                <span
                                  className="santral-waiting-queue"
                                  title="NetGSM sıradaki çağrıyı yönlendirir; yalnızca en üstteki alınabilir."
                                >
                                  Sırada
                                </span>
                              ) : null}
                            </li>
                            );
                          })}
                        </ul>
                      ) : kuyruk.bekleyenAdet > 0 ? (
                        <p className="santral-queue-waiting-hint">Bekleyen çağrı var (numara bilgisi yükleniyor…)</p>
                      ) : null}

                      {kuyruk.ustlenebilir && !izleme && (kuyruk.beklemedeCallerlar?.length ?? 0) > 1 ? (
                        <p className="santral-queue-fifo-hint">
                          <i className="fas fa-info-circle" aria-hidden />
                          {' '}
                          Birden fazla bekleyen varken yalnızca en üstteki alınabilir; NetGSM sıradaki çağrıyı yönlendirir.
                        </p>
                      ) : null}

                      {ustleniyor && ustlenilenKuyruk === kuyruk.ad && !gelenAramaGoster ? (
                        <p className="santral-queue-pickup-status">
                          <i className="fas fa-spinner fa-spin" aria-hidden /> Çağrı yönlendiriliyor, telefonunuz çalacak…
                        </p>
                      ) : null}

                      {kuyruk.dahililer.length > 0 ? (
                        <ul className="santral-agent-list">
                          {kuyruk.dahililer.map((d) => (
                            <li key={d.dahili} className={`santral-agent-item santral-agent-item--${d.durum}`}>
                              <span className="santral-agent-dot" />
                              <span className="santral-agent-name">Dahili {d.dahili}</span>
                              <span className="santral-agent-status-tag">
                                {d.molada ? 'Molada' : DURUM_LABEL[d.durum] || d.durum}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ))
                )}
                {sonGuncelleme ? (
                  <p className="santral-queue-updated">
                    Son güncelleme: {new Date(sonGuncelleme).toLocaleTimeString('tr-TR')}
                  </p>
                ) : null}
              </div>
            ) : null}
              </div>
            )}
          </section>
        </div>

        <footer className="santral-panel-footer">
          {izleme ? (
            <span
              className={`santral-connection-status${
                apiAktif === false ? ' santral-connection-status--error' : ' santral-connection-status--ok'
              }`}
            >
              <i className={`fas fa-${apiAktif === false ? 'exclamation-triangle' : 'eye'}`} aria-hidden />
              {apiAktif === false ? 'Kuyruk verisi alınamıyor' : 'İzleme modu — canlı kuyruk'}
            </span>
          ) : (
            <span
              className={`santral-connection-status${
                connectionState === 'error'
                  ? ' santral-connection-status--error'
                  : isRegistered
                    ? ' santral-connection-status--ok'
                    : ' santral-connection-status--pending'
              }`}
            >
              <i className={`fas fa-${isRegistered ? 'link' : connectionState === 'error' ? 'exclamation-triangle' : 'plug'}`} aria-hidden />
              {baglantiMetni}
            </span>
          )}
          {!izleme ? (
          <div className="santral-panel-footer-actions">
            <button
              type="button"
              className="santral-footer-icon-btn"
              onClick={() => setGelenModalAcik(true)}
              title="Gelen çağrılar"
              aria-label={`Gelen çağrılar${gelenCagriGecmisi.length ? ` (${gelenCagriGecmisi.length})` : ''}`}
            >
              <i className="fas fa-phone-volume" aria-hidden />
              {gelenCagriGecmisi.length > 0 ? (
                <span className="santral-footer-icon-btn__badge">{gelenCagriGecmisi.length > 99 ? '99+' : gelenCagriGecmisi.length}</span>
              ) : null}
            </button>
            <button
              type="button"
              className="santral-footer-icon-btn"
              onClick={() => setLogModalAcik(true)}
              title="Santral logları"
              aria-label={`Santral logları${santralLoglari.length ? ` (${santralLoglari.length})` : ''}`}
            >
              <i className="fas fa-clipboard-list" aria-hidden />
              {santralLoglari.length > 0 ? (
                <span className="santral-footer-icon-btn__badge santral-footer-icon-btn__badge--log">{santralLoglari.length > 99 ? '99+' : santralLoglari.length}</span>
              ) : null}
            </button>
          </div>
          ) : null}
        </footer>
      </aside>

      {isOpen ? (
        <div className="santral-panel-overlay" onClick={onClose} aria-hidden="true" />
      ) : null}

      {logModalAcik ? (
        <div
          className="santral-modal-overlay"
          role="presentation"
          onClick={() => setLogModalAcik(false)}
        >
          <div
            className="santral-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="santral-log-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="santral-modal-header">
              <h3 id="santral-log-modal-title" className="santral-modal-title">
                <i className="fas fa-clipboard-list" aria-hidden /> Santral logları
              </h3>
              <div className="santral-modal-header-actions">
                {santralLoglari.length > 0 ? (
                  <button
                    type="button"
                    className="santral-modal-text-btn"
                    onClick={clearSantralLoglari}
                  >
                    Temizle
                  </button>
                ) : null}
                <button
                  type="button"
                  className="santral-modal-close"
                  onClick={() => setLogModalAcik(false)}
                  aria-label="Kapat"
                >
                  <i className="fas fa-times" aria-hidden />
                </button>
              </div>
            </header>
            <div className="santral-modal-body">
              {santralLoglari.length === 0 ? (
                <p className="santral-modal-empty">Henüz log kaydı yok.</p>
              ) : (
                <ul className="santral-log-list">
                  {santralLoglari.map((log) => (
                    <li key={log.id} className={`santral-log-item santral-log-item--${log.seviye || 'info'}`}>
                      <span className="santral-log-item__time">{saatSaniyeFormat(log.zaman)}</span>
                      <i className={`fas fa-${LOG_SEVIYE_ICON[log.seviye] || LOG_SEVIYE_ICON.info}`} aria-hidden />
                      <span className="santral-log-item__mesaj">{log.mesaj}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {gelenModalAcik ? (
        <div
          className="santral-modal-overlay"
          role="presentation"
          onClick={() => setGelenModalAcik(false)}
        >
          <div
            className="santral-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="santral-gelen-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="santral-modal-header">
              <h3 id="santral-gelen-modal-title" className="santral-modal-title">
                <i className="fas fa-phone-volume" aria-hidden /> Gelen çağrılar
              </h3>
              <button
                type="button"
                className="santral-modal-close"
                onClick={() => setGelenModalAcik(false)}
                aria-label="Kapat"
              >
                <i className="fas fa-times" aria-hidden />
              </button>
            </header>
            <div className="santral-modal-body">
              {gelenCagriGecmisi.length === 0 ? (
                <p className="santral-modal-empty">Henüz gelen çağrı kaydı yok.</p>
              ) : (
                <ul className="santral-gelen-list">
                  {gelenCagriGecmisi.map((cagri) => {
                    const adSoyad = getAdSoyad(cagri.telefon);
                    const gosterim = adSoyad || formatTelefonGoster(cagri.telefon);
                    return (
                      <li key={cagri.id} className="santral-gelen-item">
                        <span className="santral-gelen-item__ad">{gosterim}</span>
                        <span className="santral-gelen-item__saat">{saatFormat(cagri.zaman)}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default SantralPanel;
